import { FocusEvent, useState } from 'react';
import {
  CountrySelector,
  defaultCountries,
  FlagImage,
  usePhoneInput,
} from 'react-international-phone';
import { ValueFieldProps } from '@sitecore-jss/sitecore-jss-react-forms';
import classNames from 'classnames';

import { TelephoneWithDialCodeViewModel } from '../../types/telephoneWithDialCode';

const TspTelephoneWithDialCode: React.FC<ValueFieldProps<TelephoneWithDialCodeViewModel>> = (
  props
) => {
  const { field, isValid, errors, tracker } = props;
  const { id, name } = field.valueField;
  const { countryCodeLabel, phoneNumberLabel, hintLabel, placeholderText, required } = field.model;
  const FieldValidationErrors = props.fieldValidationErrorsComponent;

  const [hasFocus, setHasFocus] = useState(false);
  const [hasCountryFocus, setHasCountryFocus] = useState(false);

  const { inputValue, country, setCountry, handlePhoneValueChange, inputRef } = usePhoneInput({
    defaultCountry: 'us',
    value: props.value || '',
    countries: defaultCountries,
    disableDialCodeAndPrefix: true,
    onChange: ({ phone }) => {
      props.onChange(name, phone, isValid, []);
    },
  });

  const hasAnyError = !!errors?.length;

  const onFocus = (event: FocusEvent<HTMLInputElement>) => {
    tracker.onFocusField(field, event?.target?.value);
    setHasFocus(true);
  };

  const onBlur = (event: FocusEvent<HTMLInputElement>) => {
    tracker.onBlurField(field, event?.target?.value);
    setHasFocus(false);
  };

  return (
    <>
      <div className="grid grid-cols-[2fr_3fr] gap-x-3">
        {(!!countryCodeLabel || !!phoneNumberLabel) && (
          <>
            <div>
              {!!countryCodeLabel && (
                <div className="mb-2 text-sm font-medium text-[var(--tsp-text-primary)]">
                  <label htmlFor={id}>{countryCodeLabel}</label>
                  {required && (
                    <span className="pl-0.5 font-medium text-[var(--tsp-text-primary)]">*</span>
                  )}
                </div>
              )}
            </div>
            <div>
              {!!phoneNumberLabel && (
                <div className="mb-2 text-sm font-medium text-[var(--tsp-text-primary)]">
                  <label htmlFor={id}>{phoneNumberLabel}</label>
                  {required && (
                    <span className="pl-0.5 font-medium text-[var(--tsp-text-primary)]">*</span>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="min-w-0">
          <CountrySelector
            selectedCountry={country.iso2}
            onSelect={({ iso2 }) => setCountry(iso2)}
            buttonContentWrapperStyle={{ display: 'none' }}
            renderButtonWrapper={({ children, rootProps }) => (
              <button
                {...rootProps}
                type="button"
                onFocus={() => setHasCountryFocus(true)}
                onBlur={() => setHasCountryFocus(false)}
                className={classNames(
                  'tsp-country-selector flex h-[3.25rem] w-full items-center gap-2 rounded-lg border border-[var(--tsp-border)] bg-[var(--tsp-input-bg)] px-3 py-3 outline-none',
                  hasCountryFocus && 'border-[var(--tsp-focus-border)]',
                  hasAnyError && 'border-[var(--tsp-error)] bg-[var(--tsp-error-bg)]'
                )}
              >
                <FlagImage iso2={country.iso2} size="24px" />
                <span className="tsp-country-code shrink-0 whitespace-nowrap text-base text-[var(--tsp-text-primary)]">
                  +{country.dialCode}
                </span>
                <span className="tsp-country-name min-w-0 truncate text-sm text-[var(--tsp-text-secondary)]">
                  {country.name}
                </span>
                {children}
              </button>
            )}
          />
        </div>

        <div className="min-w-0">
          <input
            type="tel"
            id={id}
            name={name}
            ref={inputRef}
            value={inputValue}
            onChange={handlePhoneValueChange}
            placeholder={placeholderText}
            required={required}
            onFocus={onFocus}
            onBlur={onBlur}
            className={classNames(
              'h-[3.25rem] w-full rounded-lg border border-[var(--tsp-border)] bg-[var(--tsp-input-bg)] px-4 py-3 text-lg text-[var(--tsp-text-primary)] outline-none',
              hasFocus && 'border-[var(--tsp-focus-border)]',
              hasAnyError && 'border-[var(--tsp-error)] bg-[var(--tsp-error-bg)]'
            )}
          />
        </div>
      </div>

      {!!hintLabel && <div className="tsp-phone-hint">{hintLabel}</div>}

      {!!FieldValidationErrors && <FieldValidationErrors {...props} />}
    </>
  );
};

export default TspTelephoneWithDialCode;
